import React, { useState, useEffect } from "react";
import { IoLocationOutline } from "react-icons/io5";
import LocationIcon from "../../assets/location.png";
import PlacesAutocomplete, { geocodeByAddress } from "react-places-autocomplete";
import { FaTimesCircle } from "react-icons/fa";
import clearIcon from "../../assets/Cancel.png";

interface LocationDetail {
  formatted_address?: string;
  lat?: number;
  lng?: number;
  postalcode?: string;
  country?: string;
  state?: string;
  city?: string;
  locality?: string;
  sublocality?: string;
  route?: string;
  street?: string;
}

interface Props {
  tripType?: string;
  locationDetail: (location: LocationDetail, error?: string) => void;
  value: string;
  disabled?: boolean;
  onBlur?: (e: any) => void;
  onChange?: (e: any) => void;
  clearInput: () => void; // Passed from parent to clear input
}

const FromCityLocationSearchInput: React.FC<Props> = ({ tripType, locationDetail, value, disabled, clearInput }) => {
  const [address, setAddress] = useState<string>(value);
  const [fieldError, setFieldError] = useState<string>("");

  const handleChange = (address: string) => {
    setAddress(address);
    setFieldError("");
  };

  const handleSelect = async (address: string) => {
    setAddress(address);
    let locationDetailObj: LocationDetail = {};

    try {
      const response = await geocodeByAddress(address);
      response.forEach((value) => {
        locationDetailObj.formatted_address = value.formatted_address;
        locationDetailObj.lat = value.geometry.location.lat();
        locationDetailObj.lng = value.geometry.location.lng();
        value.address_components.forEach((location: any) => {
          if (location.types.includes("postal_code")) locationDetailObj.postalcode = location.long_name;
          if (location.types.includes("country")) locationDetailObj.country = location.long_name;
          if (location.types.includes("administrative_area_level_1")) locationDetailObj.state = location.long_name;
          if (location.types.includes("administrative_area_level_2")) locationDetailObj.city = location.long_name;
          if (location.types.includes("locality")) locationDetailObj.locality = location.long_name;
          if (location.types.includes("sublocality")) locationDetailObj.sublocality = location.long_name;
          if (location.types.includes("route")) locationDetailObj.route = location.long_name;
          if (location.types.includes("street_number")) locationDetailObj.street = location.long_name;
        });
      });

      if (locationDetailObj.formatted_address && locationDetailObj.lat && locationDetailObj.lng) {
        locationDetail(locationDetailObj);
      } else {
        console.error("Location detail fetching failed.");
      }
    } catch (error) {
      console.error("Failed to fetch location details:", error);
    }
  };

  const clearField = () => {
    setAddress("");  // Clears the address field in the child component
    clearInput();    // Calls the clear function passed from the parent
  };

  return (
    <div>
      <div className="flex flex-col w-full md:w-[100%]">
        {/* Title */}
        <p className="font-semibold text-left text-gray-800 lg:text-sm md:text-sm text-sm uppercase mb-1 ml-2">PICK-UP LOCATION</p>

        {/* Input Container */}
        <div className="bg-gray-200 rounded-full px-3 flex items-center w-full">
          <img src={LocationIcon} alt="Location" className="w-3 h-3 mr-2 text-red-500" />
          <PlacesAutocomplete
            value={value} // Controlled by Formik
            onChange={(address) => locationDetail({ formatted_address: address })}
            onSelect={handleSelect}
            highlightFirstSuggestion={true}
          >
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
              <div className="relative w-full">
                <input
                  {...getInputProps({
                    placeholder: "City, Airport, Station, etc",
                    className: "bg-transparent border-0 focus:outline-none w-full text-gray-500 text-xs",
                  })}
                  disabled={disabled}
                />

                {/* Suggestions Dropdown */}
                <div className="autocomplete-dropdown-container absolute left-1/2 transform -translate-x-[45%] w-[90vw] max-w-[500px] z-10 mt-2 bg-white shadow-lg rounded-lg"
                style={{width:'90%'}}
                >
                  {loading && <div className="p-2">Loading...</div>}
                  {suggestions.map((suggestion, index) => {
                    const className = suggestion.active
                      ? "suggestion-item--active bg-gray-100 p-2 rounded-lg"
                      : "suggestion-item p-2 rounded-lg";
                    return (
                      <div
                        {...getSuggestionItemProps(suggestion, { className })}
                        key={index}
                        className="p-2 cursor-pointer"
                      >
                        <span className="flex items-center gap-2">
                          <IoLocationOutline className="text-red-500 shrink-0 w-5 h-5" />
                          <div className="text-left overflow-hidden flex-1 max-w-[470px]">
                            {suggestion.description}
                          </div>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </PlacesAutocomplete>

          {/* Clear Input Button */}
          {value && (
            <>
              <img src={clearIcon} onClick={clearInput} className="cursor-pointer ml-2 w-3 h-3" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FromCityLocationSearchInput;
